import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Hand, Globe, SlidersHorizontal, Users, Check, GraduationCap } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import ExperienceCard from "../components/ExperienceCard";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1706844210879-19ad6c554d78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjaGlsZHJlbiUyMG1hcmluZSUyMGJpb2xvZ3klMjBvY2VhbiUyMGV4cGxvcmF0aW9uJTIwZmllbGQlMjB0cmlwfGVufDB8fHx8MTc5MDMyNDYwOXww&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  { icon: Hand, t: "Hands-On Learning", d: "Educational experiences that encourage students to explore, discover, and engage." },
  { icon: Globe, t: "Real-World Experiences", d: "Opportunities to connect academic concepts with practical experiences beyond the classroom." },
  { icon: SlidersHorizontal, t: "Flexible Participation", d: "Choose monthly memberships, individual experiences, or premium destination trips." },
  { icon: Users, t: "Homeschool Community", d: "Connect with other homeschool families through shared educational adventures." },
];

const FREQ = { monthly: "/month", annual: "/year", quarterly: "/quarter" };

export default function Home() {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/experiences").then((r) => {
      setExperiences(r.data.filter((e) => new Date(e.date) >= new Date()).slice(0, 3));
    }).catch(() => {});
    api.get("/membership-plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[88vh] w-full overflow-hidden">
        <img src={HERO} alt="Students exploring" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mx-auto flex min-h-[88vh] max-w-7xl flex-col justify-center px-5 py-24 lg:px-8">
          <span className="eyebrow text-exploration fade-up">Homeschool Enrichment Academy</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl fade-up">
            Learning Beyond<br />the Classroom.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/80 fade-up">
            Discover hands-on educational experiences, exciting field trips, and real-world exploration designed to enrich your homeschool journey.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row fade-up">
            <Button data-testid="hero-memberships-button" onClick={() => navigate("/memberships")}
              className="h-13 bg-brandblue px-8 py-6 font-display text-base font-semibold hover:bg-blue-700">
              Explore Memberships <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button data-testid="hero-experiences-button" onClick={() => navigate("/experiences")} variant="outline"
              className="h-13 border-white/30 bg-white/10 px-8 py-6 font-display text-base font-semibold text-white backdrop-blur hover:bg-white/20">
              View Upcoming Experiences
            </Button>
          </div>
          <div className="mt-14 flex flex-wrap gap-x-6 gap-y-2 fade-up">
            {["Field Experiences", "Creative Arts", "Technology", "Career Exploration"].map((c, i) => (
              <React.Fragment key={c}>
                {i > 0 && <span className="hidden text-white/30 sm:inline">·</span>}
                <span className="eyebrow text-white/70">{c}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="text-center">
          <span className="eyebrow text-brandblue">Why Choose H.E.A.?</span>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy lg:text-4xl">Learning Goes Further Here.</h2>
        </div>
        <div className="mt-12 grid gap-7 md:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((v) => (
            <div key={v.t} className="rounded-3xl border border-slate-200 bg-white p-7 card-lift">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brandblue/10 text-brandblue"><v.icon className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-lg font-bold text-navy">{v.t}</h3>
              <p className="mt-2 text-sm text-slateBrand">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP PREVIEW */}
      <section className="topo-bg border-y border-slate-200 bg-cloud">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <span className="eyebrow text-brandblue">Monthly Memberships</span>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy lg:text-4xl">Choose Your Next Adventure.</h2>
          </div>
          <div className="mt-12 grid gap-7 md:grid-cols-3">
            {plans.map((p) => (
              <div key={p.id} data-testid={`home-plan-${p.id}`} className={`rounded-3xl border bg-white p-8 ${p.category === "CORE" ? "border-brandblue shadow-xl" : "border-slate-200"}`}>
                <span className="eyebrow text-brandblue">{p.category}</span>
                <h3 className="mt-2 font-display text-2xl font-bold text-navy">{p.name}</h3>
                <div className="mt-3 font-display text-4xl font-extrabold text-navy">${p.price}<span className="text-base font-semibold text-slateBrand">{FREQ[p.billing_frequency] || ""}</span></div>
                <div className="mt-1 text-sm font-semibold text-brandblue">{p.trips_label}</div>
                <p className="mt-4 text-sm text-slateBrand">{p.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button data-testid="home-view-pricing-btn" onClick={() => navigate("/memberships")} className="bg-brandblue font-display font-semibold hover:bg-blue-700">
              View Full Pricing <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURED EXPERIENCES */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <span className="eyebrow text-brandblue">Upcoming</span>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy lg:text-4xl">Explore Something New</h2>
          </div>
          <Button variant="ghost" data-testid="home-view-all-btn" className="hidden font-display font-semibold text-brandblue sm:inline-flex" onClick={() => navigate("/experiences")}>
            View all <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
          {experiences.map((e) => <ExperienceCard key={e.id} exp={e} />)}
        </div>
      </section>

      {/* STEP UP SECTION */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-navy p-10 lg:p-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-7 w-7 text-gold" />
              <span className="eyebrow text-gold">Using Step Up For Students?</span>
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold text-white lg:text-4xl">Reserve Scholarship Funds</h2>
            <p className="mt-4 text-white/70">Learn how to locate Homeschool Enrichment Academy in the EMA Marketplace, select an approved service, reserve funds, and finalize your registration.</p>
            <p className="mt-3 text-sm text-white/50">Eligibility and availability depend on current program requirements and approved provider listings. Not all services or accounts are automatically eligible.</p>
            <Button data-testid="home-stepup-btn" onClick={() => navigate("/step-up-booking")} className="mt-8 bg-gold font-display font-semibold text-navy hover:bg-gold/90">
              View Step Up Booking Guide <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
